import React, { useState, useRef, useEffect } from 'react';
import { useGetThreatActorDirectoryQuery, useGetThreatOverviewQuery } from '@/lib/services/threatFeedsApi';
import { Filter, RotateCcw, ChevronDown, Search, AlertTriangle, X, MapPin, Globe, Briefcase, ChevronUp, Copy, CheckCircle2, Loader2 } from 'lucide-react';

// --- IMPORT YOUR EXISTING COMPONENTS ---
import TargetedSectorsChart from '@/components/features/threat-feeds/TargetedSectorsChart';
import GlobalThreatMap from '@/components/features/threat-feeds/GlobalThreatMap';

// --- CUSTOM DROPDOWN COMPONENT ---
interface DropdownProps {
    label: string;
    value: string;
    options: string[];
    onChange: (val: string) => void;
    searchable?: boolean;
}

const FilterDropdown: React.FC<DropdownProps> = ({ label, value, options, onChange, searchable = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt => opt.toLowerCase().includes(query.toLowerCase()));

    return (
        <div className="relative" ref={dropdownRef}>
            <div onClick={() => setIsOpen(!isOpen)} className={`bg-[#0A0B0E] border ${isOpen ? 'border-[#50A970]' : 'border-[#1A1D24]'} text-slate-400 text-[11px] px-4 py-2.5 rounded-lg flex items-center justify-between min-w-[140px] cursor-pointer hover:border-slate-500 transition-colors`}>
                <span>{label}: <span className="text-white ml-1">{value}</span></span>
                <ChevronDown className={`w-3 h-3 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#50A970]' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[160px] bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.8)] z-50 overflow-hidden flex flex-col">
                    {searchable && (
                        <div className="p-2 border-b border-[#1A1D24]">
                            <div className="bg-[#13171F] rounded flex items-center px-2 py-1.5">
                                <Search className="w-3 h-3 text-slate-500 mr-2 shrink-0" />
                                <input type="text" placeholder="Type to filter" value={query} onChange={(e) => setQuery(e.target.value)} className="bg-transparent border-none text-[11px] text-white focus:outline-none w-full placeholder-slate-600" />
                            </div>
                        </div>
                    )}
                    <div className="max-h-[200px] overflow-y-auto custom-scrollbar p-1">
                        <div onClick={() => { onChange(`All ${label.split(' ')[0]}s`); setIsOpen(false); setQuery(''); }} className={`px-3 py-2 text-[11px] rounded cursor-pointer transition-colors ${value.startsWith('All') ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-300 hover:bg-[#161A23]'}`}>
                            All {label === 'Sort By' ? 'Options' : label.split(' ')[0]}s
                        </div>
                        {filteredOptions.map((opt) => (
                            <div key={opt} onClick={() => { onChange(opt); setIsOpen(false); setQuery(''); }} className={`px-3 py-2 text-[11px] rounded cursor-pointer transition-colors ${value === opt ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-300 hover:bg-[#161A23]'}`}>
                                {opt}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- DATA PARSING HELPERS ---
const safeParseArray = (data: any) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.warn("Failed to parse array data:", e);
            return [];
        }
    }
    return [];
};

const COUNTRY_ALIASES: Record<string, string> = {
    "united states": "United States of America",
    "usa": "United States of America",
    "uk": "United Kingdom",
    "russia": "Russian Federation",
};

// --- MAIN COMPONENT ---
const ThreatActorDirectory: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState({ search: '', sort: 'Most Active', status: 'All Statuses', ttp: 'All TTPs' });

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [combinedActors, setCombinedActors] = useState<any[]>([]);

    // Infinite Scroll Ref Boundary Trigger
    const observerSentinelRef = useRef<HTMLDivElement | null>(null);

    // Modal States
    const [selectedActor, setSelectedActor] = useState<any | null>(null);
    const [operationsExpanded, setOperationsExpanded] = useState(true);
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const isFilterActive = filters.search !== '' || filters.sort !== 'Most Active' || filters.status !== 'All Statuses' || filters.ttp !== 'All TTPs';

    // Query Hook utilizing param object definition format
    const { data: directoryData, isLoading: isDirectoryLoading, isFetching: isDirectoryFetching } = useGetThreatActorDirectoryQuery({ page: currentPage });
    const { data: overviewData, isLoading: isOverviewLoading } = useGetThreatOverviewQuery();

    // Type casting inline to suppress stale build structure contracts
    const typedDirectoryData = directoryData as any;
    const rawActors = typedDirectoryData?.threat_actor_directory || [];
    const summary = typedDirectoryData?.directory_summary;
    const pagination = typedDirectoryData?.pagination;

    // Reset accumulated collection and stream back page offset to 1 when filter metrics alter
    useEffect(() => {
        setCurrentPage(1);
        setCombinedActors([]);
    }, [filters]);

    // Append new lazy payload collections into state container safely
    useEffect(() => {
        if (rawActors.length > 0) {
            if (currentPage === 1) {
                setCombinedActors(rawActors);
            } else {
                setCombinedActors(prev => {
                    const existingIds = new Set(prev.map(a => a.name));
                    const uniqueNewActors = rawActors.filter((a: any) => !existingIds.has(a.name));
                    return [...prev, ...uniqueNewActors];
                });
            }
        }
    }, [rawActors, currentPage]);

    // Viewport IntersectionObserver for robust layout detection
    useEffect(() => {
        if (isDirectoryFetching || !pagination?.has_next) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '150px' }
        );

        const currentSentinel = observerSentinelRef.current;
        if (currentSentinel) {
            observer.observe(currentSentinel);
        }

        return () => {
            if (currentSentinel) {
                observer.unobserve(currentSentinel);
            }
        };
    }, [isDirectoryFetching, pagination]);

    const lastSyncString = overviewData?.meta?.last_sync_utc ? `${overviewData.meta.last_sync_utc} UTC` : 'AWAITING TELEMETRY...';
    const systemStatus = overviewData?.status ? overviewData.status.toUpperCase() : 'ELEVATED';
    const statusColorClass = systemStatus === 'SUCCESS' ? 'text-[#50A970]' : 'text-[#FF3333]';

    // Filter & Sorting execution framework over the local persistent streaming buffer
    let displayedActors = [...combinedActors];
    if (filters.search) displayedActors = displayedActors.filter(actor => actor.name?.toLowerCase().includes(filters.search.toLowerCase()));
    if (filters.status !== 'All Statuses') displayedActors = displayedActors.filter(actor => actor.status?.toLowerCase() === filters.status.toLowerCase());
    if (filters.ttp !== 'All TTPs') displayedActors = displayedActors.filter(actor => safeParseArray(actor.vectors).some((v: any) => v.value.toLowerCase() === filters.ttp.toLowerCase()));

    if (filters.sort === 'Most Active') displayedActors.sort((a, b) => (b.recorded_incidents || 0) - (a.recorded_incidents || 0));
    else if (filters.sort === 'Recently Seen') displayedActors.sort((a, b) => new Date(b.last_seen || 0).getTime() - new Date(a.last_seen || 0).getTime());
    else if (filters.sort === 'Alphabetical') displayedActors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const handleSearchClick = () => setFilters(prev => ({ ...prev, search: searchInput }));
    const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && handleSearchClick();
    const handleReset = () => { setSearchInput(''); setFilters({ search: '', sort: 'Most Active', status: 'All Statuses', ttp: 'All TTPs' }); };

    const getPrimaryTTP = (vectors: any[]) => {
        if (!vectors || vectors.length === 0) return 'Unknown';
        return vectors.reduce((max, obj) => (max.count > obj.count) ? max : obj).value;
    };

    const getMaxVectorCount = (vectors: any[]) => {
        if (!vectors || vectors.length === 0) return 1;
        return Math.max(...vectors.map(v => v.count));
    };

    const copyToClipboard = (text: string | null) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const parsedIndustries = safeParseArray(selectedActor?.victim_industries);
    const parsedGeography = safeParseArray(selectedActor?.geography);
    const parsedVectors = safeParseArray(selectedActor?.vectors);
    const parsedOperations = safeParseArray(selectedActor?.recent_operations);

    const mappedIndustries = parsedIndustries.map((ind: any) => ({
        industry_name: ind.value,
        attack_count: ind.count
    }));

    const mappedGeography = parsedGeography.map((geo: any) => ({
        id: COUNTRY_ALIASES[geo.value.toLowerCase()] || geo.value,
        value: geo.count
    }));

    return (
        <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono relative">
            
            {/* ✅ GLOBAL PIPELINE INGESTION MODAL OVERLAY */}
            {isDirectoryLoading && currentPage === 1 && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[99999] flex flex-col items-center justify-center gap-4 animate-in fade-in duration-300 pl-[70px] lg:pl-[240px]">
                    <div className="p-6 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl flex flex-col items-center max-w-xs text-center shadow-2xl relative overflow-hidden">
                        <div className="absolute -top-10 -left-10 w-24 h-24 bg-[#50A970]/5 rounded-full pointer-events-none blur-xl"></div>
                        <Loader2 className="h-9 w-9 text-[#50A970] animate-spin mb-3.5" />
                        <h3 className="text-[12px] font-bold text-white uppercase tracking-wider">Syncing Adversary Registry</h3>
                        <p className="text-[11px] text-slate-500 font-mono mt-1.5 leading-relaxed">
                            Downloading threat actors' records from global endpoint telemetry workers...
                        </p>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-end mb-2">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Threats Feeds</h1>
                    <p className={`text-[10px] mt-1 font-semibold uppercase tracking-widest ${statusColorClass}`}>System_Status: {isOverviewLoading ? 'SYNCING...' : systemStatus}</p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Last_Sync: {isOverviewLoading ? 'Fetching...' : lastSyncString}</span>
                    <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                </div>
            </div>

            <div className="flex items-center gap-3 w-full relative z-30">
                <input type="text" placeholder="Search threat actors..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={handleKeyDown} className="flex-1 bg-[#0A0B0E] border border-[#1A1D24] text-sm text-white px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#50A970] transition-colors placeholder-slate-600" />
                <button onClick={handleSearchClick} className="bg-[#50A970] hover:bg-[#3d8556] text-black font-bold px-6 py-2.5 rounded-lg transition-colors text-[13px]">Search</button>
                <button className={`border px-5 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2 pointer-events-none ${isFilterActive ? 'bg-[#50A970]/10 border-[#50A970] text-[#50A970]' : 'bg-[#0A0B0E] border-[#1A1D24] text-slate-300 opacity-50'}`}><Filter className="w-4 h-4" /> {isFilterActive ? 'Filters Active' : 'No Filters'}</button>
                <button onClick={handleReset} className="bg-[#0A0B0E] border border-[#1A1D24] hover:bg-[#161A23] text-slate-300 px-5 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Reset All</button>
            </div>

            <div className="flex flex-wrap items-center gap-3 relative z-20">
                <FilterDropdown label="Sort By" value={filters.sort} options={['Most Active', 'Recently Seen', 'Alphabetical']} onChange={(val) => setFilters(prev => ({ ...prev, sort: val }))} />
                <FilterDropdown label="Status" value={filters.status} options={['Active', 'Dormant']} onChange={(val) => setFilters(prev => ({ ...prev, status: val }))} />
                <FilterDropdown label="TTPs" value={filters.ttp} options={['Ransomware', 'Data Breach', 'Data Leak', 'Defacement', 'Initial Access', 'DDoS Attack']} onChange={(val) => setFilters(prev => ({ ...prev, ttp: val }))} />
            </div>

            <div className="flex flex-col gap-4 relative z-10 mt-2">
                {displayedActors.length === 0 ? (
                    <div className="py-12 flex items-center justify-center text-xs text-slate-600 border border-dashed border-[#1A1D24] rounded-xl">No threat actors match the current filters.</div>
                ) : (
                    displayedActors.map((actor, idx) => {
                        const parsedActorVectors = safeParseArray(actor.vectors);
                        return (
                            <div key={idx} onClick={() => setSelectedActor(actor)} className="bg-[#0A0B0E] border border-[#1A1D24] hover:border-slate-700 transition-colors p-6 rounded-xl flex flex-col relative group cursor-pointer overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-[#50A970]/0 to-[#50A970]/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                <div className="flex justify-between items-start mb-4 relative z-10">
                                    <div>
                                        <div className="text-[10px] text-slate-500 font-medium tracking-widest mb-1">{actor.last_seen ? actor.last_seen.split(' ')[0] : 'UNKNOWN'}</div>
                                        <h3 className="text-lg font-bold text-white tracking-wide">{actor.name}</h3>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Recorded Incidents</div>
                                        <div className="text-xl font-bold text-white">{actor.recorded_incidents?.toLocaleString() || 0}</div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-1 relative z-10">
                                    <span className={`text-[10px] px-2.5 py-1 rounded font-bold uppercase tracking-wider ${actor.status?.toLowerCase() === 'active' ? 'bg-[#113311] text-[#50A970] border border-[#50A970]/30' : 'bg-[#1A1D24] text-slate-400 border border-slate-700'}`}>{actor.status || 'Unknown'}</span>
                                    {parsedActorVectors.slice(0, 6).map((vector: any, vIdx: number) => (
                                        <span key={vIdx} className="bg-[#2A1111] border border-[#FF3333]/20 text-[#FF3333] text-[9px] px-2 py-1 rounded font-semibold tracking-wider uppercase">{vector.value}</span>
                                    ))}
                                </div>
                            </div>
                        )
                    })
                )}
            </div>

            {/* Infinite Scroll Reference Element */}
            <div ref={observerSentinelRef} className="h-1 w-full" />

            {/* Subtle Tracking Status Loader for Lazy Page Streaming */}
            {isDirectoryFetching && currentPage > 1 && (
                <div className="w-full py-6 flex justify-center items-center gap-2 text-slate-500 text-xs">
                    <Loader2 className="h-4 w-4 text-[#50A970] animate-spin" />
                    <span>Loading Next Threat Actor Record Batch...</span>
                </div>
            )}

            {!isDirectoryLoading && summary && (
                <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 mt-4 relative z-10">
                    <h3 className="text-[14px] font-bold text-white tracking-wide mb-6">Directory Summary</h3>
                    <div className="grid grid-cols-3 gap-6 border-b border-[#1A1D24]/50 pb-6 mb-6">
                        <div>
                            <div className="text-[11px] text-slate-500 font-medium tracking-wide mb-2">Total Groups</div>
                            <div className="text-2xl font-bold text-white">{summary.total_groups?.toLocaleString() || 0}</div>
                        </div>
                        <div>
                            <div className="text-[11px] text-slate-500 font-medium tracking-wide mb-2">Active Groups</div>
                            <div className="text-2xl font-bold text-white">{summary.active_groups?.toLocaleString() || 0}</div>
                        </div>
                        <div>
                            <div className="text-[11px] text-slate-500 font-medium tracking-wide mb-2">Total Incidents Tracked</div>
                            <div className="text-2xl font-bold text-white">{summary.total_incidents?.toLocaleString() || 0}</div>
                        </div>
                    </div>
                    <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-5">
                        <h4 className="text-[12px] font-bold text-white tracking-wide mb-2">Analyst Note</h4>
                        <p className="text-[11px] text-slate-400 font-sans leading-relaxed">
                            Groups marked as Dormant have not been observed posting new victims in the last 90 days. Active groups are ranked by total volume of confirmed victims.
                        </p>
                    </div>
                </div>
            )}

            {/* ADVERSARY MATRIX DETAIL VIEW FRAME MODAL LAYOUT OVERLAY */}
            {selectedActor && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 pl-[70px] lg:pl-[420px] transition-all duration-300">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_60px_rgba(0,0,0,0.95)] rounded-2xl w-full max-w-[620px] max-h-[90vh] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                        <div className="p-6 pb-5 border-b border-[#1A1D24]/60 relative shrink-0 overflow-hidden bg-gradient-to-b from-[#13171F] to-transparent">
                            <svg className="absolute top-0 left-0 w-64 h-64 -translate-x-1/4 -translate-y-1/4 opacity-[0.03] pointer-events-none" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="30" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="20" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="10" fill="none" stroke="white" strokeWidth="1.5" />
                            </svg>

                            <button onClick={() => setSelectedActor(null)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors p-1 z-10">
                                <X className="w-5 h-5" />
                            </button>

                            <div className="w-12 h-12 top-4 -right-4 border border-[#1A1D24] bg-[#0A0B0E] rounded-full flex items-center justify-center mb-4 relative z-10 shadow-inner">
                                <AlertTriangle className="w-5 h-5 text-slate-300" />
                            </div>

                            <h2 className="text-xl font-bold text-white mb-2 leading-tight pr-8 relative z-10">{selectedActor.name}</h2>
                            <span className={`relative z-10 inline-block text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${selectedActor.status?.toLowerCase() === 'active'
                                ? 'bg-[#113311] text-[#50A970] border border-[#50A970]/30'
                                : 'bg-[#1A1D24] text-slate-400 border border-slate-700'
                                }`}>
                                {selectedActor.status || 'Unknown'}
                            </span>
                        </div>

                        <div className="p-6 flex flex-col gap-5 overflow-y-auto custom-scrollbar flex-1 bg-[#0A0B0E]">

                            <div className="flex gap-4 shrink-0">
                                <div className="flex-1 bg-[#12151C] border border-[#1A1D24] rounded-xl p-4 shadow-lg">
                                    <div className="text-[10px] text-slate-500 font-mono tracking-widest mb-1">Confirmed Victims</div>
                                    <div className="text-xl font-bold text-white">{selectedActor.confirmed_victims?.toLocaleString() || 0}</div>
                                </div>
                                <div className="flex-1 bg-[#12151C] border border-[#1A1D24] rounded-xl p-4 shadow-lg">
                                    <div className="text-[10px] text-slate-500 font-mono tracking-widest mb-1">Primary TTP</div>
                                    <div className="text-sm font-bold text-white mt-1.5 truncate" title={getPrimaryTTP(parsedVectors)}>{getPrimaryTTP(parsedVectors)}</div>
                                </div>
                            </div>

                            <div className="w-full rounded-xl border border-[#1A1D24] bg-[#12151C] p-4 shadow-lg overflow-hidden shrink-0">
                                <div className="w-full overflow-x-auto custom-scrollbar">
                                    <div className="w-full min-w-[500px]">
                                        <TargetedSectorsChart data={mappedIndustries} isLoading={false} />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-4 shadow-lg flex flex-col font-mono relative overflow-hidden h-[280px] shrink-0">
                                <div className="mb-2 shrink-0">
                                    <h2 className="text-[12px] font-bold tracking-wide text-white">Geography</h2>
                                </div>
                                <div className="overflow-x-auto custom-scrollbar w-full rounded-lg border border-[#1A1D24]/50 flex-1">
                                    <div className="min-w-[500px] h-full relative bg-[#0A0B0E]">
                                        {mappedGeography.length > 0 ? (
                                            <GlobalThreatMap data={mappedGeography} isLoading={false} />
                                        ) : (
                                            <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs font-sans italic">No geographic targeting data recorded.</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-5 shadow-lg shrink-0">
                                <h3 className="text-[12px] font-bold text-white mb-4 tracking-wide uppercase">Vectors</h3>
                                <div className="flex flex-col gap-3">
                                    {parsedVectors.length > 0 ? (
                                        parsedVectors.slice(0, 5).map((vector: any, vIdx: number) => {
                                            const max = getMaxVectorCount(parsedVectors);
                                            const percent = Math.max((vector.count / max) * 100, 2);
                                            return (
                                                <div key={vIdx}>
                                                    <div className="flex justify-between items-end text-[10px] font-mono mb-1">
                                                        <span className="text-slate-300">{vector.value}</span>
                                                        <span className="text-slate-400">{vector.count.toLocaleString()}</span>
                                                    </div>
                                                    <div className="w-full h-1 bg-[#1A1D24] rounded-full overflow-hidden">
                                                        <div className="h-full bg-[#50A970] rounded-full transition-all duration-1000 ease-out" style={{ width: `${percent}%` }}></div>
                                                    </div>
                                                </div>
                                            )
                                        })
                                    ) : (
                                        <div className="text-slate-500 text-xs italic font-sans py-1">No vector data recorded.</div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl shadow-lg overflow-hidden transition-all shrink-0">
                                <button onClick={() => setOperationsExpanded(!operationsExpanded)} className="w-full flex justify-between items-center p-4 hover:bg-[#161A23] transition-colors">
                                    <h3 className="text-[12px] font-bold text-white tracking-wide uppercase">Recent Operations</h3>
                                    <ChevronUp className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-300 ${operationsExpanded ? '' : 'rotate-180'}`} />
                                </button>

                                {operationsExpanded && (
                                    <div className="flex flex-col gap-3 px-4 pb-4">
                                        {parsedOperations.length > 0 ? (
                                            parsedOperations.slice(0, 3).map((op: any, oIdx: number) => (
                                                <div
                                                    key={oIdx}
                                                    onClick={() => { if (op.published_url) setPendingUrl(op.published_url); }}
                                                    className={`bg-[#0A0B0E] border border-[#1A1D24] p-3.5 rounded-lg flex flex-col gap-2 transition-colors ${op.published_url ? 'cursor-pointer hover:border-[#FF3333]/50' : ''}`}
                                                >
                                                    <div className="flex justify-between items-start pointer-events-none">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <h4 className="text-[12px] font-bold text-white tracking-wide truncate">
                                                                {op.victim_site && op.victim_site !== 'Unknown' ? op.victim_site : op.victim_organization && op.victim_organization !== 'Unknown' ? op.victim_organization : 'Unknown Target'}
                                                            </h4>
                                                            {op.category && (
                                                                <span className="bg-[#2A1111] text-[#FF3333] border border-[#FF3333]/20 text-[8px] px-1.5 py-0.5 rounded font-semibold tracking-wider shrink-0 uppercase">
                                                                    {op.category}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-[9px] text-slate-500 font-mono tracking-widest shrink-0">{op.published_at?.split(' ')[0]}</span>
                                                    </div>

                                                    <p className="text-[10px] text-slate-400 font-sans italic line-clamp-1 pointer-events-none">"{op.title}"</p>

                                                    <div className="flex flex-wrap items-center gap-3 text-[9px] text-slate-500 font-sans mt-0.5 pointer-events-none">
                                                        {op.victim_country && op.victim_country !== 'Unknown' && (
                                                            <div className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {op.victim_country}</div>
                                                        )}
                                                        {op.victim_industry && op.victim_industry !== 'Unknown' && (
                                                            <div className="flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" /> {op.victim_industry}</div>
                                                        )}
                                                        {op.network && op.network !== 'Unknown' && (
                                                            <div className="flex items-center gap-1"><Globe className="w-2.5 h-2.5" /> {op.network}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-4 text-slate-500 text-[10px] italic border border-dashed border-[#1A1D24] rounded-lg bg-[#0A0B0E]">
                                                No recent operations detected for this threat actor.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end mt-1">
                                <button onClick={() => setSelectedActor(null)} className="px-5 py-2 rounded-lg bg-[#161A23] border border-[#1A1D24] hover:bg-[#1A1D24] text-slate-300 text-[11px] font-bold transition-colors">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- EXTERNAL URL REDIRECTION WARNING MODAL --- */}
            {pendingUrl && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_60px_rgba(255,51,51,0.15)] rounded-2xl w-full max-w-[420px] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-5">

                        <svg className="absolute top-0 left-0 w-64 h-64 -translate-x-1/4 -translate-y-1/4 opacity-[0.05] pointer-events-none" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="30" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="20" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="10" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                        </svg>

                        <button onClick={() => { setPendingUrl(null); setCopied(false); }} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors p-1 z-10">
                            <X className="w-5 h-5" />
                        </button>

                        <div className="w-11 h-11 border top-4 left-4 border-[#FF3333]/30 bg-[#FF3333]/10 rounded-xl flex items-center justify-center mb-4 relative z-10 shadow-inner">
                            <AlertTriangle className="w-4 h-4 text-[#FF3333]" />
                        </div>

                        <h2 className="text-[16px] font-sans font-bold text-white mb-2 relative z-10">External Redirection Warning</h2>
                        <p className="text-[11px] text-slate-400 font-mono leading-relaxed mb-4 relative z-10">
                            You are about to leave the platform and visit an external URL. This link often leads to a hacker forum, leak site, or unverified source.
                        </p>

                        <div className="bg-[#12151C] border border-[#FF3333]/30 rounded-lg p-3 mb-4 relative z-10 flex items-center gap-3 overflow-hidden shadow-inner">
                            <p className="text-[11px] text-[#FF6B6B] font-mono truncate flex-1" title={pendingUrl}>{pendingUrl}</p>
                            <button
                                onClick={() => copyToClipboard(pendingUrl)}
                                className="text-slate-500 hover:text-white transition-colors shrink-0 p-1"
                                title="Copy URL"
                            >
                                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                        </div>

                        <p className="text-[10px] text-slate-500 font-mono leading-relaxed mb-5 relative z-10">
                            Security Tip: We recommend opening this link in a sandboxed environment or a dedicated VM.
                        </p>

                        <div className="flex justify-end gap-3 relative z-10">
                            <button
                                onClick={() => { setPendingUrl(null); setCopied(false); }}
                                className="px-4 py-2 rounded-lg bg-[#161A23] border border-[#1A1D24] hover:bg-[#1A1D24] text-slate-300 text-[11px] font-bold tracking-wide transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    window.open(pendingUrl, '_blank', 'noopener,noreferrer');
                                    setPendingUrl(null);
                                    setCopied(false);
                                }}
                                className="px-4 py-2 rounded-lg bg-[#DE3535] hover:bg-[#FF4444] text-white text-[11px] font-bold tracking-wide transition-colors"
                            >
                                Proceed (Unsafe)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
            `}} />
        </div>
    );
};

export default ThreatActorDirectory;